import React, { FC } from 'react'
import classNames from 'classnames'
import startCase from 'lodash/startCase'
import moment from 'moment'
import { TIconSize } from 'transfix-ui/components/SvgIcon/types'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { makeStyles, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { brandColors } from 'transfix-ui/theme/colors'
import { IRate, IShipmentShipperRateSource } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { AlertIcon } from 'transfix-icons'
import { FORMATS } from 'constants/datetime'

const RATE_SOURCE_LABELS: { [key: string]: string } = {
  manual_stark: 'Manual',
  manual_tms: 'TMS',
  edi_tender: 'EDI',
}

export type TRateSource = Omit<IShipmentShipperRateSource, 'rate'> & {
  rate: Pick<IRate, 'id' | 'amount'>
}

interface IRateSourcingTooltipProps {
  rateSources: TRateSource[]
  triggerClassName?: string
  iconSize?: TIconSize
  loading?: boolean
}

const useRateSourcingTooltipStyles = makeStyles(theme => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr 3fr',
    columnGap: theme.spacing(1.5),
    textAlign: 'left',
  },
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  loading: {
    '&.MuiSkeleton-root': {
      background: brandColors.coolGray1,
    },
  },
}))

const RateSourcingTooltip: FC<IRateSourcingTooltipProps> = ({
  iconSize = 'default',
  loading = false,
  rateSources,
  triggerClassName,
}) => {
  const classes = useRateSourcingTooltipStyles()
  const noDiscrepancies = rateSources.every(
    rate => rate.difference === '+0.00' || rate.difference === 'Current rate'
  )
  const alertIconColor = noDiscrepancies ? 'coolGray5' : 'warning1'
  const alertIconClassNames = classNames(triggerClassName, classes.trigger)

  const shipperRateSourcingEnabled = useFeatureFlag('shipperRateSourcing')

  if (!shipperRateSourcingEnabled || !rateSources?.length) return null

  return (
    <Tooltip
      position='bottom'
      trigger={
        <div className={alertIconClassNames}>
          <AlertIcon color={alertIconColor} size={iconSize} />
        </div>
      }>
      {loading ? (
        <Skeleton width='100px' className={classes.loading} />
      ) : (
        <div className={classes.container}>
          {rateSources.map(rateSource => {
            const {
              createdAt,
              difference,
              rate: { amount },
              rateTypeKey,
            } = rateSource
            return (
              <>
                <div>
                  <Typography variant='caption'>
                    {moment(createdAt).format(FORMATS.dayShort)}
                  </Typography>
                </div>
                <div>
                  <Typography variant='caption'>
                    {RATE_SOURCE_LABELS[rateTypeKey] || startCase(rateTypeKey)}
                  </Typography>
                </div>
                <div>
                  <Typography variant='caption'>{`$${amount} (${difference})`}</Typography>
                </div>
              </>
            )
          })}
        </div>
      )}
    </Tooltip>
  )
}

export default RateSourcingTooltip
